import { useEffect, useMemo, useState } from "react";
import {
  ChartLine,
  Bell,
  Sparkle,
  Plus,
  Minus,
  Coins,
  MapPin,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PURITY_FACTOR = {
  "24K": 1.0,
  "22K": 0.916,
  "18K": 0.75,
  "14K": 0.585,
};

const CITIES = [
  { code: "chennai", label: "Chennai" },
  { code: "mumbai", label: "Mumbai" },
  { code: "delhi", label: "Delhi" },
  { code: "bangalore", label: "Bangalore" },
  { code: "hyderabad", label: "Hyderabad" },
  { code: "kolkata", label: "Kolkata" },
];

export default function PriceCheck() {
  const [data, setData] = useState(null);
  const [city, setCity] = useState("chennai");
  const [weight, setWeight] = useState(10);
  const [purity, setPurity] = useState("22K");
  const [making, setMaking] = useState(15);
  const [stoneValue, setStoneValue] = useState(0);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertTarget, setAlertTarget] = useState(6500);
  const [alertType, setAlertType] = useState("above");
  const [alertSubmitting, setAlertSubmitting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    api
      .get(`/gold-rates?city=${city}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [city]);

  useEffect(() => {
    if (user?.email && !alertEmail) setAlertEmail(user.email);
  }, [user, alertEmail]);

  const rates = data?.rates || { "24k": 7200, "22k": 6600, "18k": 5400, silver: 95 };
  const trend = data?.trend_30d || [];
  const cityLabel =
    CITIES.find((c) => c.code === (data?.city || city))?.label || "Chennai";
  const chartData = useMemo(
    () => trend.map((v, i) => ({ day: i + 1, rate: v })),
    [trend],
  );

  const ratePerGram = useMemo(() => {
    if (purity === "24K") return rates["24k"];
    if (purity === "22K") return rates["22k"];
    if (purity === "18K") return rates["18k"];
    if (purity === "14K") return rates["18k"] * (PURITY_FACTOR["14K"] / PURITY_FACTOR["18K"]);
    return rates["22k"];
  }, [purity, rates]);

  const calc = useMemo(() => {
    const w = Number(weight) || 0;
    const m = Number(making) || 0;
    const stones = Number(stoneValue) || 0;
    const goldValue = w * ratePerGram;
    const makingCharge = goldValue * (m / 100);
    const subtotal = goldValue + makingCharge + stones;
    const gst = subtotal * 0.03;
    const total = subtotal + gst;
    return { goldValue, makingCharge, stones, subtotal, gst, total };
  }, [weight, making, stoneValue, ratePerGram]);

  const overpriceWarning = calc.total * 1.1;

  const submitAlert = async (e) => {
    e.preventDefault();
    if (!alertEmail) {
      toast.error("Email required");
      return;
    }
    if (!alertTarget || Number(alertTarget) <= 0) {
      toast.error("Enter a valid target price");
      return;
    }
    setAlertSubmitting(true);
    try {
      await api.post("/price-alerts", {
        target_price: Number(alertTarget),
        alert_type: alertType,
        metal: "22k",
        city,
        email: alertEmail,
        channel: "email",
      });
      toast.success(
        `Alert set! We'll email ${alertEmail} when ${cityLabel} 22K goes ${alertType} ₹${alertTarget}/g.`,
      );
      if (!user) setAlertEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set alert");
    } finally {
      setAlertSubmitting(false);
    }
  };

  return (
    <div data-testid="price-check-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
              Board Rate · City Wise
            </p>
            <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[#0A1128] tracking-tight">
              Know the fair price <span className="gold-text">before you walk in</span>.
            </h1>
          </div>
          <div className="shrink-0">
            <label className="text-[11px] uppercase tracking-wider text-[#4A5568] flex items-center gap-1">
              <MapPin size={12} weight="duotone" /> City
            </label>
            <select
              data-testid="city-select"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="mt-1 h-11 px-3 pr-8 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] font-medium"
            >
              {CITIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Rate cards */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[
            { k: "24K Gold", v: rates["24k"], hue: "#D4AF37" },
            { k: "22K Gold", v: rates["22k"], hue: "#3B5BDB" },
            { k: "18K Gold", v: rates["18k"], hue: "#5C3EC2" },
            { k: "Silver", v: rates.silver, hue: "#94a3b8" },
          ].map((r, i) => (
            <div
              key={r.k}
              className="mc-card lift smooth p-5"
              data-testid={`rate-card-${i}`}
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#4A5568]">
                <Coins size={14} weight="duotone" style={{ color: r.hue }} />
                {r.k}
              </div>
              <p className="font-display text-3xl mt-2 text-[#0A1128]">
                ₹{r.v.toLocaleString("en-IN")}
                <span className="text-sm text-[#4A5568] font-body"> /g</span>
              </p>
              <p className="text-[11px] text-[#4A5568] mt-1">{cityLabel} board rate · today</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          {/* Calculator */}
          <div className="mc-card p-6 sm:p-8 lg:col-span-2" data-testid="price-calculator">
            <h2 className="font-display text-2xl text-[#0A1128]">
              Jewellery Price Calculator
            </h2>
            <p className="text-sm text-[#4A5568] mt-1">
              See the full breakdown — gold, making, GST.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              {/* Weight */}
              <div>
                <label className="text-xs uppercase tracking-wider text-[#4A5568]">
                  Weight (grams)
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <button
                    data-testid="weight-minus"
                    onClick={() => setWeight((w) => Math.max(0.1, +(w - 0.5).toFixed(2)))}
                    className="h-10 w-10 rounded-md border border-[#E8DFD1] flex items-center justify-center hover:border-[#3B5BDB] smooth"
                  >
                    <Minus size={14} />
                  </button>
                  <input
                    data-testid="weight-input"
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="flex-1 h-10 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                  />
                  <button
                    data-testid="weight-plus"
                    onClick={() => setWeight((w) => +((Number(w) || 0) + 0.5).toFixed(2))}
                    className="h-10 w-10 rounded-md border border-[#E8DFD1] flex items-center justify-center hover:border-[#3B5BDB] smooth"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              {/* Purity */}
              <div>
                <label className="text-xs uppercase tracking-wider text-[#4A5568]">
                  Gold purity
                </label>
                <select
                  data-testid="purity-select"
                  value={purity}
                  onChange={(e) => setPurity(e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                >
                  {Object.keys(PURITY_FACTOR).map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </div>

              {/* Making */}
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-wider text-[#4A5568]">
                    Making charge
                  </label>
                  <span className="text-sm font-medium text-[#3B5BDB]" data-testid="making-value">
                    {making}%
                  </span>
                </div>
                <input
                  data-testid="making-slider"
                  type="range"
                  min="5"
                  max="45"
                  value={making}
                  onChange={(e) => setMaking(Number(e.target.value))}
                  className="mt-2 w-full accent-[#3B5BDB]"
                />
                <div className="flex justify-between text-[11px] text-[#4A5568] mt-1">
                  <span>5%</span><span>15% fair</span><span>30%</span><span>45%</span>
                </div>
              </div>

              {/* Stones */}
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider text-[#4A5568]">
                  Stone / diamond value (optional)
                </label>
                <input
                  data-testid="stone-value-input"
                  type="number"
                  value={stoneValue}
                  onChange={(e) => setStoneValue(e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Result breakdown */}
            <div className="mt-7 rounded-lg bg-[#F5EFE6]/40 border border-[#E8DFD1] p-5" data-testid="price-breakdown">
              <Row label={`Gold value (${weight || 0}g × ₹${ratePerGram.toLocaleString("en-IN")})`} value={calc.goldValue} />
              <Row label={`Making charge (${making}%)`} value={calc.makingCharge} />
              {calc.stones > 0 && <Row label="Stones / diamonds" value={calc.stones} />}
              <Row label="Subtotal" value={calc.subtotal} />
              <Row label="GST (3%)" value={calc.gst} />
              <div className="mt-3 pt-3 border-t border-[#E8DFD1] flex items-baseline justify-between">
                <span className="text-sm text-[#0A1128] font-medium">
                  Total fair price
                </span>
                <span className="font-display text-3xl text-[#3B5BDB] font-semibold" data-testid="price-total">
                  ₹{Math.round(calc.total).toLocaleString("en-IN")}
                </span>
              </div>
              <p className="mt-3 text-xs text-[#B45309] bg-[#EAB308]/10 px-3 py-2 rounded">
                Anything above ₹{Math.round(overpriceWarning).toLocaleString("en-IN")} is overpriced for this design.
              </p>
            </div>
          </div>

          {/* AI tip + alert */}
          <div className="space-y-6">
            <div className="mc-card p-6 bg-gradient-to-br from-[#3B5BDB]/5 to-[#D4AF37]/5" data-testid="ai-tip-box">
              <div className="flex items-center gap-2">
                <Sparkle size={18} weight="duotone" className="text-[#D4AF37]" />
                <p className="text-xs uppercase tracking-[0.2em] text-[#3B5BDB] font-semibold">
                  Mine AI tip
                </p>
              </div>
              <p className="mt-3 text-sm text-[#0A1128] leading-relaxed">
                Today's 22K {cityLabel} board rate is ₹{rates["22k"].toLocaleString("en-IN")}/g — slightly below this month's average.
                A reasonable time to buy if you need the piece now. For investment-only buys, watch a few more days.
              </p>
            </div>

            <form
              onSubmit={submitAlert}
              className="mc-card p-6"
              data-testid="price-alert-form"
            >
              <div className="flex items-center gap-2">
                <Bell size={18} weight="duotone" className="text-[#3B5BDB]" />
                <p className="font-display text-xl text-[#0A1128]">Set a price alert</p>
              </div>
              <p className="text-xs text-[#4A5568] mt-1">
                We'll email you when {cityLabel} 22K crosses your target.
              </p>
              <div className="mt-4 space-y-3">
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="alert-type-above"
                    onClick={() => setAlertType("above")}
                    className={`flex-1 py-2 rounded-md border text-sm smooth ${
                      alertType === "above"
                        ? "border-[#3B5BDB] bg-[#3B5BDB]/5 text-[#3B5BDB] font-medium"
                        : "border-[#E8DFD1]"
                    }`}
                  >
                    Above
                  </button>
                  <button
                    type="button"
                    data-testid="alert-type-below"
                    onClick={() => setAlertType("below")}
                    className={`flex-1 py-2 rounded-md border text-sm smooth ${
                      alertType === "below"
                        ? "border-[#3B5BDB] bg-[#3B5BDB]/5 text-[#3B5BDB] font-medium"
                        : "border-[#E8DFD1]"
                    }`}
                  >
                    Below
                  </button>
                </div>
                <input
                  data-testid="alert-target"
                  type="number"
                  value={alertTarget}
                  onChange={(e) => setAlertTarget(Number(e.target.value))}
                  className="w-full h-10 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                  placeholder="Target ₹/gram"
                />
                <input
                  data-testid="alert-email"
                  type="email"
                  value={alertEmail}
                  onChange={(e) => setAlertEmail(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                  placeholder="you@example.com"
                />
                <button
                  data-testid="alert-submit"
                  type="submit"
                  disabled={alertSubmitting}
                  className="w-full h-10 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60"
                >
                  {alertSubmitting ? "Setting..." : "Set alert"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Trend chart */}
        <div className="mt-10 mc-card p-6 sm:p-8" data-testid="trend-chart">
          <div className="flex items-center gap-2">
            <ChartLine size={20} weight="duotone" className="text-[#3B5BDB]" />
            <h3 className="font-display text-xl text-[#0A1128]">
              22K Board Rate · Last 30 days · {cityLabel}
            </h3>
          </div>
          <div className="h-72 mt-5">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#3B5BDB" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#3B5BDB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E8DFD1" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#4A5568" fontSize={12} />
                <YAxis stroke="#4A5568" fontSize={12} domain={["dataMin - 50", "dataMax + 50"]} />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #E8DFD1",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(v) => [`₹${v.toLocaleString("en-IN")}`, "Rate"]}
                />
                <Line
                  type="monotone"
                  dataKey="rate"
                  stroke="#3B5BDB"
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5, fill: "#D4AF37" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 text-sm">
      <span className="text-[#4A5568]">{label}</span>
      <span className="text-[#0A1128] font-medium tabular-nums">
        ₹{Math.round(value).toLocaleString("en-IN")}
      </span>
    </div>
  );
}
